import { useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Resolver, useForm, useWatch } from 'react-hook-form';

import { paths } from '@common/types';
import { debounce } from '@util/debounce.ts';
import { toastApiError } from '@common/errors/error-formatting.ts';
import {
  useAllProgramListQuery,
  usePatientQuery,
  useSaveTaskMutation,
} from '@common/cache';

import { isTaskRequirement } from '../common';
import { schemaAggregator } from 'components/DynamicFormFields/schema/schemaAggregator';
import {
  ACTIVITY_MINUTES_KEYS,
  emptyActivityMinutes,
  MAX_ACTIVITY_MINUTES,
  staticFieldsSchema,
} from '../schema';
import { InferType } from 'yup';
import { t } from 'i18next';

const REPEATER_FIELDS = ['repeater_icd10'];

const sumActivityMinutes = (
  activity_minutes: Partial<
    Record<(typeof ACTIVITY_MINUTES_KEYS)[number], number>
  >,
) =>
  ACTIVITY_MINUTES_KEYS.reduce(
    (acc, key) => acc + (Number(activity_minutes[key]) || 0),
    0,
  );

const parseStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter(item => typeof item === 'string');
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return [];
  }

  try {
    const parsed = JSON.parse(value);

    return Array.isArray(parsed)
      ? parsed.filter(item => typeof item === 'string')
      : [];
  } catch {
    return [];
  }
};

const normalizeRequirementInitialValue = (requirement: {
  type: string;
  value: unknown;
  min?: number;
}) => {
  switch (requirement.type) {
    case 'repeater_icd10': {
      const parsed = parseStringArray(requirement.value);
      const minEntries = requirement.min ?? 0;

      if (parsed.length >= minEntries) {
        return parsed;
      }

      return [
        ...parsed,
        ...Array.from({ length: minEntries - parsed.length }, () => ''),
      ];
    }

    default:
      return requirement.value ?? '';
  }
};

const toSerializableValue = (value: unknown): string | number => {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    return value;
  }

  return '';
};

const serializeRequirementValue = (
  type: string,
  value: unknown,
): string | number => {
  if (REPEATER_FIELDS.includes(type)) {
    const cleaned = Array.isArray(value)
      ? value
          .filter(v => typeof v === 'string')
          .map(v => v.trim())
          .filter(v => v !== '')
      : [];

    return JSON.stringify(cleaned);
  }

  return toSerializableValue(value);
};

const deserializeRequirementValue = (requirement: {
  type: string;
  value: DynamicRequirementValue;
  min?: number;
}) => {
  if (REPEATER_FIELDS.includes(requirement.type)) {
    const parsed = parseStringArray(requirement.value);
    const minEntries = requirement.min ?? 0;

    if (parsed.length >= minEntries) {
      return parsed;
    }

    return [
      ...parsed,
      ...Array.from({ length: minEntries - parsed.length }, () => ''),
    ];
  }

  return requirement.value ?? '';
};

const getRootFieldName = (name: string) => name.split('.')[0];

const STATIC_FIELDS = [
  'static_notes',
  'static_notable_call',
  'static_recorded_minutes',
  'static_program_id',
  'static_note_type',
  'static_activity_minutes',
];

type DynamicRequirementValue =
  | string
  | string[]
  | boolean
  | number
  | null
  | undefined
  | Partial<Record<(typeof ACTIVITY_MINUTES_KEYS)[number], number>>;

type FormShape = {
  [field_name: string]: DynamicRequirementValue;
} & InferType<typeof staticFieldsSchema>;

export const useRequirements = (
  task: paths[`/task/{task_id}`]['get']['responses']['200']['content']['application/json'],
  setIsSavingTask: (saving: boolean) => void,
  setFormValidity: (valid: boolean) => void,
) => {
  const { mutateAsync: saveTaskMutation, isPending: isSavePending } =
    useSaveTaskMutation(task.id);
  const { data: patientData } = usePatientQuery({
    patient_id: task.patient.id,
  });
  const { data: programOptions = [] } = useAllProgramListQuery();

  const { requirements, missing_fields } = task;
  const flattenedRequirements = requirements.flatMap(req => {
    if (isTaskRequirement(req)) {
      return {
        ...req,
        name: req.field_name,
        value: req.field_value,
        disable_if: req.disable_if,
        editable: req.editable,
      };
    } else {
      return req.requirements.map(req2 => ({
        ...req2,
        name: req2.field_name,
        value: req2.field_value,
        disable_if: req2.disable_if,
        editable: req2.editable,
      }));
    }
  });
  const initialValues = flattenedRequirements.reduce<Record<string, unknown>>(
    (acc, current) => {
      acc[current.name] = normalizeRequirementInitialValue(current);
      return acc;
    },
    {},
  );

  const schema = schemaAggregator(flattenedRequirements);

  const formMethods = useForm<FormShape>({
    mode: 'onChange',
    resolver: yupResolver(staticFieldsSchema.concat(schema)) as Resolver<
      FormShape,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      FormShape
    >,
    defaultValues: {
      ...initialValues,
      static_notes: task.notes,
      static_note_type: task.note_type,
      static_notable_call: task.notable_call,
      static_recorded_minutes:
        task.activity_minutes_mode || task.recorded_minutes
          ? (task.recorded_minutes ?? 0)
          : undefined,
      static_program_id: task.program,
      static_activity_minutes: {
        ...emptyActivityMinutes,
        ...(task.activity_minutes ?? {}),
      },
    },
    shouldUseNativeValidation: false,
  });

  useEffect(() => {
    if (task.completion_date) {
      formMethods.reset({
        ...initialValues,
        static_notes: task.notes,
        static_note_type: task.note_type,
        static_notable_call: task.notable_call,
        static_recorded_minutes: task.recorded_minutes ?? 0,
        static_program_id: task.program,
        static_activity_minutes: {
          ...emptyActivityMinutes,
          ...(task.activity_minutes ?? {}),
        },
      });
    }
  }, [task.completion_date]);

  // A save is queued from the moment a field changes until the debounced
  // request actually leaves; `isSavePending` takes over from there.
  const [isSaveQueued, setIsSaveQueued] = useState(false);

  useEffect(() => {
    setIsSavingTask(isSaveQueued || isSavePending);
  }, [isSaveQueued, isSavePending]);

  // The tab unmounts on tab change: never leave the drawer stuck in "saving".
  useEffect(() => () => setIsSavingTask(false), []);

  const watchedValues = useWatch({ control: formMethods.control });

  // eslint-disable-next-line react-hooks/immutability -- intentional: updating RHF context for cross-field yup validation, this is the documented pattern
  formMethods.control._options.context = {
    values: watchedValues,
    hideDefaultControls: task?.hide_default_controls,
  };

  const { trigger, getValues, setValue } = formMethods;

  const formValues = getValues();

  useEffect(() => {
    trigger().then(valid => setFormValidity(valid));
  }, [formValues]);

  useEffect(() => {
    trigger();
  }, [task?.hide_default_controls]);

  const [fieldName, setFieldName] = useState('');
  const pendingFieldNamesRef = useRef<Set<string>>(new Set());

  const handleChangeField = async (nameList: string[]) => {
    if (nameList.length > 0) {
      const clearInterval = fieldName === nameList[0];
      setFieldName(nameList[0]);

      // Accumulate all changed field names across cancelled requests
      nameList.forEach(name => pendingFieldNamesRef.current.add(name));

      setIsSaveQueued(true);
      debounce(async () => {
        // Snapshot and clear pending names before the request
        const accumulatedNames = [...pendingFieldNamesRef.current];
        pendingFieldNamesRef.current.clear();
        // `isSavePending` flips to true in the same batch as this reset, so the
        // saving state never dips to false between the two.
        setIsSaveQueued(false);

        try {
          const normalizedNames = [
            ...new Set(accumulatedNames.map(getRootFieldName)),
          ];

          const formPayload = normalizedNames.reduce<
            Record<string, string | number>
          >((acc, curr) => {
            if (STATIC_FIELDS.includes(curr)) {
              return acc;
            }

            const requirement = flattenedRequirements.find(
              o => o.name === curr,
            );
            const rawValue = getValues(curr);

            acc[curr] = requirement
              ? serializeRequirementValue(requirement.type, rawValue)
              : toSerializableValue(rawValue);

            return acc;
          }, {});

          const response = await saveTaskMutation({
            form_data: formPayload,
            notes: getValues('static_notes') || '',
            notable_call: !!getValues('static_notable_call'),
            recorded_minutes:
              getValues('static_recorded_minutes') != null &&
              !isNaN(Number(getValues('static_recorded_minutes')))
                ? Number(getValues('static_recorded_minutes'))
                : null,
            note_type: getValues(
              'static_note_type',
            ) as paths['/task/{task_id}/save']['post']['requestBody']['content']['application/json']['note_type'],
            ...(task.program_editable && {
              program: getValues(
                'static_program_id',
              ) as paths['/task/{task_id}/save']['post']['requestBody']['content']['application/json']['program'],
            }),
            ...(task.activity_minutes_mode && {
              activity_minutes: {
                ...emptyActivityMinutes,
                ...(getValues('static_activity_minutes') ?? {}),
              },
            }),
            events: [],
          });

          if (response.data && response.data.updated_fields?.length > 0) {
            const { updated_fields } = response.data;
            updated_fields.forEach(updatedField => {
              const requirement = flattenedRequirements.find(
                o => o.field_name === updatedField.field_name,
              );

              setValue(
                updatedField.field_name,
                requirement
                  ? deserializeRequirementValue({
                      ...requirement,
                      value: updatedField.field_value,
                      min: updatedField.min,
                      type: updatedField.type,
                    })
                  : (updatedField.field_value ?? ''),
                {
                  shouldDirty: false,
                  shouldTouch: false,
                  shouldValidate: true,
                },
              );
            });
          }
        } catch (error) {
          // If the request was cancelled, restore the names so the next request includes them
          if (accumulatedNames.length > 0) {
            accumulatedNames.forEach(name =>
              pendingFieldNamesRef.current.add(name),
            );
          }
          toastApiError(error);
        }
      }, clearInterval);
    }
  };

  const activityLabels: Record<(typeof ACTIVITY_MINUTES_KEYS)[number], string> =
    {
      plan: t(
        'worklist.worklistItem.requirements.otherActivities.carePlanUpdate',
      ),
      reconciliate: t(
        'worklist.worklistItem.requirements.otherActivities.medicationReconciliation',
      ),
      coordinate: t(
        'worklist.worklistItem.requirements.otherActivities.careCoordination',
      ),
      educate: t(
        'worklist.worklistItem.requirements.otherActivities.patientPoaEducation',
      ),
      communicate: t(
        'worklist.worklistItem.requirements.otherActivities.communicationForPatientRequests',
      ),
    };

  const handleActivityChange = (
    key: (typeof ACTIVITY_MINUTES_KEYS)[number],
    value: number,
  ) => {
    const current = {
      ...emptyActivityMinutes,
      ...(getValues('static_activity_minutes') ?? {}),
    };
    const othersSum = ACTIVITY_MINUTES_KEYS.reduce<number>(
      (acc, k) => (k === key ? acc : acc + (Number(current[k]) || 0)),
      0,
    );
    const cappedValue = Math.max(
      0,
      Math.min(value, MAX_ACTIVITY_MINUTES - othersSum),
    );
    const next = {
      ...current,
      [key]: cappedValue,
    };
    setValue('static_activity_minutes', next, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('static_recorded_minutes', sumActivityMinutes(next), {
      shouldDirty: true,
      shouldValidate: true,
    });
    handleChangeField(['static_recorded_minutes']);
  };

  const noteTypeOptions = [
    'Email',
    'Phone call',
    'In person',
    'Reflection',
    'Text',
    'Other',
  ].map(value => ({
    value,
    label: value,
  }));

  const isCCMLimitWarningDisplayed =
    patientData?.programs.map(o => o.name).includes('CCM') &&
    (patientData.ccm_minutes_this_month ?? 0) >= 180;

  return {
    formMethods,
    handleChangeField,
    missing_fields,
    noteTypeOptions,
    isFormComplete: !task.has_write_permission,
    isCCMLimitWarningDisplayed,
    programOptions: programOptions.map(o => ({
      value: o.name,
      label: o.name,
    })),
    handleActivityChange,
    activityLabels,
  };
};
